/**
 * LocationZoomLabel — a facility-level name badge anchored at the location
 * center, revealed only once the camera pulls back far enough that the field
 * itself is too small to read.
 *
 * Positioned imperatively on camera move (no React re-render per frame),
 * mirroring CenterPointOverlay. Both the ramp view and the event planner mount
 * it inside `MapboxScene`'s `renderOverlay`, so cinema mode hides it for free.
 */
import { useEffect, useRef } from 'react'
// Named type import (not the default namespace) so `noUnusedLocals` stays quiet.
import type { Map as MapboxMap } from 'mapbox-gl'

interface LocationZoomLabelProps {
  map: MapboxMap | null
  /** Scene origin [lng, lat] — the facility anchor. */
  center: [number, number] | null
  /** Primary label, e.g. "Augusta Regional". */
  name: string | null | undefined
  /** Optional short code rendered under the name, e.g. "AGS". */
  code?: string | null
  /** Zoom at (and above) which the label is fully hidden. */
  hideAboveZoom?: number
  /** Zoom at (and below) which the label is fully opaque. */
  showBelowZoom?: number
}

const OFFSCREEN = 'translate3d(-9999px,-9999px,0)'

/** Park the badge while the anchor sits outside the canvas by this margin (px). */
const CULL_MARGIN = 96

export function LocationZoomLabel({
  map,
  center,
  name,
  code,
  hideAboveZoom = 15,
  showBelowZoom = 13.5,
}: LocationZoomLabelProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!map || !center || !name) return

    const park = (el: HTMLDivElement) => {
      el.style.opacity = '0'
      el.style.transform = OFFSCREEN
    }

    const update = () => {
      const el = ref.current
      if (!el) return

      // Linear ramp between the two thresholds: 0 above hideAboveZoom, 1 below
      // showBelowZoom. Guard the degenerate span so a caller passing equal
      // thresholds gets a hard cutoff rather than a divide-by-zero.
      const zoom = map.getZoom()
      const span = hideAboveZoom - showBelowZoom
      const opacity =
        span <= 0
          ? zoom < hideAboveZoom
            ? 1
            : 0
          : Math.min(1, Math.max(0, (hideAboveZoom - zoom) / span))

      if (opacity <= 0) {
        park(el)
        return
      }

      const p = map.project(center)
      const canvas = map.getCanvas()
      if (
        p.x < -CULL_MARGIN ||
        p.y < -CULL_MARGIN ||
        p.x > canvas.clientWidth + CULL_MARGIN ||
        p.y > canvas.clientHeight + CULL_MARGIN
      ) {
        park(el)
        return
      }

      el.style.opacity = String(opacity)
      el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate(-50%, -50%)`
    }

    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }

    // `zoom` fires `move` too, but keyboard/scroll zooms that settle without a
    // pan still need the ramp recomputed — cheap enough to bind both.
    map.on('move', schedule)
    map.on('zoom', schedule)
    update()

    return () => {
      map.off('move', schedule)
      map.off('zoom', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [map, center, name, hideAboveZoom, showBelowZoom])

  if (!center || !name) return null

  return (
    <div className="pointer-events-none absolute inset-0 z-[7] overflow-hidden">
      <div
        ref={ref}
        className="absolute left-0 top-0"
        style={{
          transform: OFFSCREEN,
          opacity: 0,
          willChange: 'transform, opacity',
          // NOT `paint` — this element is sized to the 12x12 dot, and paint
          // containment would clip the pill that hangs below it.
          contain: 'layout style',
        }}
      >
        {/* The dot sits exactly on the anchor; the pill hangs below it. */}
        <div className="relative flex h-3 w-3 items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-sky-400/20" />
          <span className="h-1.5 w-1.5 rounded-full bg-sky-300 shadow-[0_0_8px_rgba(56,189,248,0.9)]" />
          <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-black/70 px-3 py-1.5 text-center backdrop-blur-sm">
            <div className="text-sm font-bold leading-tight text-white">{name}</div>
            {code && (
              <div className="text-[10px] font-semibold uppercase leading-tight tracking-[0.18em] text-white/45">
                {code}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
